import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { useIntl } from 'react-intl-next';
import { bindAll } from 'bind-event-listener';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { CommsCrafterAgent, JobWriterAgent, A11yMateAgent, getEditorAgent } from '@atlassian/generative-ai-modal/utils/agents';
import { isConvoAIKnownMessageTemplate } from '../types';
import { AnalyticsFlowContextProvider, useAnalyticsFlow, useCreateAnalyticsFlow } from '../analytics/analytics-flow/analyticsFlowUtils';
import { ModalRegionErrorBoundary } from '../ui/components/ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { useAIExperienceService } from './useAIExperienceService';
// not imported as messages as there are messages being imported from elsewhere currently
// this is being unwound in another chunk of work
import { messages as experienceMessages } from './messages';
import { LoadingScreenWithLogic } from './screens-with-logic/LoadingScreenWithLogic';
import { RateLimitScreenWithLogic } from './screens-with-logic/RateLimitScreenWithLogic';
import { ApiFailedScreenWithLogic } from './screens-with-logic/ApiFailedScreenWithLogic';
import { FailedScreenWithLogic } from './screens-with-logic/FailedScreenWithLogic';
import { PreviewScreenWithLogic } from './screens-with-logic/PreviewScreenWithLogic';
import { DiscardScreenWithLogic } from './screens-with-logic/DiscardScreenWithLogic';
import { UserInputCommandPaletteWithLogic } from './screens-with-logic/UserInputCommandPaletteWithLogic';
import { mapLoadingStatusToMessageTemplate } from './screens-with-logic/utils/map-loading-status-to-message-template';
import { analyticsFormatMessage } from '../utils/format-message';
export function ExperienceApplicationStateManager(props) {
  var _context$configItem, _context$configItem$g;
  /**
   * Note -- because we are on an old version of xstate -- when adding new states
   * they will not automatically be typed.
   *
   * You need to add these in the `AIExperienceMachineState` type in the
   * `get-experience-service.ts` file
   **/
  const state = props.state;
  /**
   * Note -- because we are on an old version of xstate -- when adding new events
   * they will not automatically be typed.
   *
   * You need to add these in the `AIExperienceMachineEvent` type in the
   * `get-experience-service.ts` file
   **/
  const send = props.send;
  const context = props.context;
  const {
    formatMessage
  } = useIntl();
  React.useEffect(() => {
    // Note -- the intention is to have the state machine created at a higher
    // level, and have the positions updated without being passed into this react
    // component.
    send({
      type: 'update positions',
      positions: props.positions
    });
  }, [send, props.positions]);
  React.useEffect(() => {
    if (state === 'started state') {
      send({
        type: 'kick off'
      });
    }
  }, [state, send]);

  /**
   * Used to pass the backendModel to analytics and
   * feedback submission metadata
   */
  const backendModel = context === null || context === void 0 ? void 0 : (_context$configItem = context.configItem) === null || _context$configItem === void 0 ? void 0 : (_context$configItem$g = _context$configItem.getBackendModel) === null || _context$configItem$g === void 0 ? void 0 : _context$configItem$g.call(_context$configItem, !!(context !== null && context !== void 0 && context.latestPromptResponse));
  const analyticsFlow = useAnalyticsFlow();
  React.useEffect(() => {
    analyticsFlow.updateCommonAttributes({
      backendModel
    });
  }, [analyticsFlow, backendModel]);

  // Note -- the intention is to move the creation of the state machine higher
  // and for the ExperienceApplication (react component that appears in the modal)
  // to only be started once the machine is in the modal active state.
  if (state === 'started state') {
    return null;
  }
  const isModalActiveState = typeof state !== 'string' && state['modal active state'];
  if (!isModalActiveState) {
    // TODO AIFOLLOWUP
    // ERROR handler
    throw new Error('unhandled state');
  }
  const experienceState = state['modal active state']['experience'];
  if (typeof state['modal active state'] === 'object' && state['modal active state']['discard'] === 'active') {
    return /*#__PURE__*/React.createElement(DiscardScreenWithLogic, {
      state: experienceState,
      endExperience: props.endExperience,
      send: send,
      editCount: context.editCount,
      retryPromptCount: context.retryPromptCount,
      history: context.responseHistory
    });
  }
  if (typeof experienceState === 'string' && experienceState === 'command palette state') {
    return /*#__PURE__*/React.createElement(UserInputCommandPaletteWithLogic, {
      editorPluginAIProvider: props.editorPluginAIProvider,
      send: send,
      endExperience: props.endExperience,
      context: context
    });
  }
  if (typeof experienceState === 'object' && 'loading state' in experienceState) {
    const loadingState = experienceState['loading state'];
    if (loadingState === 'loading') {
      const getLoadingMessage = status => {
        // TODO AIFOLLOWUP
        // Add analytics for when we get an unknown message descriptor
        if (!status || !isConvoAIKnownMessageTemplate(status)) {
          return status;
        }
        const messageTemplate = mapLoadingStatusToMessageTemplate(status);
        if (messageTemplate) {
          return formatMessage(messageTemplate);
        }
        return status;
      };
      const loadingMessage = getLoadingMessage(context === null || context === void 0 ? void 0 : context.loadingStatus);
      return /*#__PURE__*/React.createElement(LoadingScreenWithLogic, {
        title: loadingMessage ? loadingMessage : formatMessage(experienceMessages.loadingTitle),
        markdown: context.markdown,
        editorView: props.editorView,
        schema: props.editorView.state.schema,
        send: send,
        editorPluginAIProvider: props.editorPluginAIProvider,
        latestPrompt: context.latestPrompt,
        promptTrigger: context.promptTrigger,
        latestPromptRetried: context.latestPromptRetried,
        idMap: context.idMap,
        providerFactory: props.providerFactory,
        contextStatistics: context.contextStatistics
      });
    }
    if (loadingState === 'rate limit active' || loadingState === 'rate limit inactive') {
      return /*#__PURE__*/React.createElement(RateLimitScreenWithLogic, {
        loadingState: loadingState,
        send: send,
        endExperience: props.endExperience,
        errorInfo: context.apiErrorInfo
      });
    }
    if (loadingState === 'api failed state') {
      return /*#__PURE__*/React.createElement(ApiFailedScreenWithLogic, {
        send: send,
        endExperience: props.endExperience,
        errorInfo: context.apiErrorInfo
      });
    }
  }
  if (typeof experienceState !== 'string' && 'review state' in experienceState) {
    return /*#__PURE__*/React.createElement(PreviewScreenWithLogic, {
      userInput: context.userInput || '',
      userADFInput: context.userADFInput || '',
      isInputActive: experienceState['review state'] === 'refinement',
      aupViolation: context.aupViolation,
      markdown: context.markdown,
      schema: props.editorView.state.schema,
      editorPluginAIProvider: props.editorPluginAIProvider,
      editorView: props.editorView,
      positions: props.positions,
      configItem: context.configItem,
      endExperience: props.endExperience,
      send: send,
      latestPrompt: context.latestPrompt,
      appearance: props.appearance,
      product: props.editorPluginAIProvider.product,
      lastTriggeredFrom: props.lastTriggeredFrom,
      editCount: context.editCount,
      retryPromptCount: context.retryPromptCount,
      idMap: context.idMap,
      providerFactory: props.providerFactory,
      history: context.responseHistory,
      contextStatistics: context.contextStatistics,
      backendModel: backendModel,
      errorInfo: context.apiErrorInfo,
      promptTrigger: context.promptTrigger
    });
  }
  if (experienceState === 'failed') {
    return /*#__PURE__*/React.createElement(FailedScreenWithLogic, {
      failureReason: context.failureReason,
      endExperience: props.endExperience,
      errorInfo: context.apiErrorInfo
    });
  }
  throw new Error('unhandled state');
}
function ExperienceApplicationStateWrapper(props) {
  const intl = useIntl();
  const modalRef = React.useRef(null);
  const {
    context,
    state,
    send
  } = useAIExperienceService({
    context: {
      aiProvider: props.editorPluginAIProvider,
      intl,
      editorView: props.editorView,
      positions: props.positions,
      baseGenerate: props.configItem.selectionType === 'empty' ? props.editorPluginAIProvider.emptyConfig.baseGenerate : props.editorPluginAIProvider.rangeConfig.baseGenerate,
      configItem: props.configItem,
      markdown: '',
      aupViolation: false,
      latestPromptResponse: null,
      editCount: 0,
      retryPromptCount: 0,
      latestPromptRetried: false,
      userInput: '',
      apiErrorInfo: {},
      responseHistory: {
        positionFromEnd: 0,
        entries: []
      },
      agents: []
    },
    endExperience: props.endExperience
  });

  // Request information about the latest Rovo agents and update the state accordingly
  React.useEffect(() => {
    if (!getBooleanFF('platform.editor.ai-rovo-agents')) {
      return;
    } else if (!props.editorPluginAIProvider.getChannelVariables) {
      // Flag can be on, but off for this specific editor
      // eg. Confluence only wants it on for full page editors, not comment editors
      return;
    }

    // TODO: EDF-767: Fetch agent information from Rovo
    // Not entirely sure what data we'll get, but we can mould it as needed
    const agents = [CommsCrafterAgent, JobWriterAgent, A11yMateAgent].map(getEditorAgent);
    send({
      type: 'update agents',
      context: {
        agents
      }
    });
  }, [send, props.editorPluginAIProvider.getChannelVariables]);
  React.useEffect(() => {
    if (!modalRef.current) {
      return;
    }

    /**
     * KeyDown (as opposed to KeyPress) event is used to handle the escape key so that
     * other escape key handlers will not override this behaviour.
     * Additionally this aligns with other escape listeners e.g. for closing typeahead popup.
     */
    function handleEscapeKeyDown(event) {
      // Exit early if the key is not Escape
      if (event.key !== 'Escape') {
        return;
      }

      // Continue only if the AI modal is open / in active state
      if (typeof state === 'string' || !('modal active state' in state)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      // AI TODO REFACTOR as part of ED-17975. For now, this fixes the streaming
      // cancellation that happened when relying solely on the 'escape' event
      const modalActiveState = state === null || state === void 0 ? void 0 : state['modal active state'];
      const experience = modalActiveState === null || modalActiveState === void 0 ? void 0 : modalActiveState.experience;
      if (typeof experience === 'object' && 'loading state' in experience && experience['loading state'] === 'loading') {
        if ((modalActiveState === null || modalActiveState === void 0 ? void 0 : modalActiveState.discard) === 'active') {
          send({
            type: 'disable discard'
          });
        } else {
          send({
            type: 'enable discard'
          });
        }
      } else {
        send({
          type: 'escape'
        });
      }
    }

    /**
     * Handle KeyUp event to prevent unexpected escape behaviours when
     * there are other escape handlers outside of component (e.g. in product)
     */
    function handleEscapeKeyUp(event) {
      // Exit early if the key is not Escape
      if (event.key !== 'Escape') {
        return;
      }

      // Continue only if the AI modal is open / in active state
      if (typeof state === 'string' || !('modal active state' in state)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    }
    return bindAll(modalRef.current, [{
      type: 'keydown',
      listener: handleEscapeKeyDown
    }, {
      type: 'keyup',
      listener: handleEscapeKeyUp
    }]);
  }, [modalRef, state, send]);
  return /*#__PURE__*/React.createElement("div", {
    // We set these to ensure that when focus initially moves to
    // an element within the modal, that context about the modal
    // (e.g. "Atlassian Intelligence Dialog") is announced too
    role: "dialog"
    // ED-19787
    // We would like to use aria-labelledBy so we can label this
    // dynamically for each screen, however it's not working as expected
    // in Chrome and Firefox (works in Safari). For now we'll use the dialog
    // role and make the first element of each screen a screen-reader accessible
    // heading for the experience.
    // aria-label={intl.formatMessage(experienceMessages.dialogAriaLabel)}
    ,
    ref: modalRef
  }, /*#__PURE__*/React.createElement(ExperienceApplicationStateManager, _extends({}, props, {
    state: state,
    context: context,
    send: send
  })));
}
export function ExperienceApplication(props) {
  const analyticsFlow = useCreateAnalyticsFlow({
    invokeAttributes: {
      invokedFrom: props.lastTriggeredFrom,
      triggerMethod: props.triggerMethod,
      experienceName: props.configItem.title({
        formatMessage: analyticsFormatMessage
      })
    }
  });

  // finish analytics flow and fire remaining steps on unmount
  React.useEffect(() => {
    return () => {
      // have to postpone until animation frame to make sure we end analytics flow after child components unmount
      // which is where we enqueue last events
      window.requestAnimationFrame(() => {
        analyticsFlow.fireQueuedStep();
      });
    };
  }, [analyticsFlow]);

  // This makes sure that the selection preview is hidden when the experience is unmounted
  React.useEffect(() => {
    return () => {
      const tr = props.editorView.state.tr;
      tr.setMeta('hideSelectionPreview', true);
      props.editorView.dispatch(tr);
    };
  }, [props.editorView]);
  return /*#__PURE__*/React.createElement(ModalRegionErrorBoundary, {
    onCloseFallback: () => {
      props.endExperience();
    }
  }, /*#__PURE__*/React.createElement(AnalyticsFlowContextProvider, {
    value: analyticsFlow
  }, /*#__PURE__*/React.createElement(ExperienceApplicationStateWrapper, props)));
}